import React, { useState } from 'react';
import { Button } from '@ordermentum/ui-shared/atoms/Button/Button';
import Spinner from '@ordermentum/ui-shared/atoms/Spinner';
import { errorComponent } from '@ordermentum/ui-shared/molecules/FormErrorComponent';
import {
  EditableInput,
  EditableField,
} from '@ordermentum/ui-shared/atoms/Field';
import { Row, Cell } from '@ordermentum/ui-shared/atoms/Grid';
import FormCheckbox from '@ordermentum/ui-shared/atoms/FormCheckbox';
import Select from '@ordermentum/ui-shared/atoms/SelectV2';
import {
  RetailerRegisterForm,
  RetailerRegisterFormComponentProps,
} from '@ordermentum/ui-shared/organisms/RetailerRegister';
import { User } from '@ordermentum/ui-types';
import { AUS_STATES } from '@ordermentum/ui-constants';
import { changeWindowLocation } from '../../../lib/utilities';
import {
  TermsAndConditionContainer,
  SubText,
  OrangeText,
  CenterContainer,
} from '../../Shared/Form/Form.style';

type Props = {
  user: User;
};

type InternalProps = {
  isRedirecting: boolean;
};

const CustomRetailerRegisterForm =
  ({ isRedirecting }: InternalProps) =>
  ({
    isSameBilling,
    setSameBilling,
    error,
    formik: {
      setFieldTouched,
      values,
      touched,
      errors,
      setFieldValue,
      handleSubmit,
      isSubmitting,
    },
  }: RetailerRegisterFormComponentProps) => {
    const onBlur = (field: string) => setFieldTouched(field, true);

    return (
      <form onSubmit={handleSubmit}>
        {error && errorComponent({}, { msg: error })}
        <h3>Company Details</h3>
        <Row>
          <Cell>
            <EditableInput
              required
              label="Venue Trading name"
              labelWidth="12rem"
              tag="tradingName"
              value={values.tradingName}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              required
              label="Legal/Corporate Entity"
              labelWidth="12rem"
              tag="legalName"
              value={values.legalName}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              label="ABN"
              labelWidth="12rem"
              tag="abn"
              value={values.abn}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              required
              label="Phone"
              labelWidth="12rem"
              tag="phone"
              value={values.phone}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <h3>Delivery Address</h3>
        <Row>
          <Cell>
            <EditableInput
              required
              label="Street Address 1"
              labelWidth="12rem"
              tag="deliveryStreetAddress1"
              value={values.deliveryStreetAddress1}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              label="Street Address 2"
              labelWidth="12rem"
              tag="deliveryStreetAddress2"
              value={values.deliveryStreetAddress2}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              required
              label="Suburb"
              labelWidth="12rem"
              tag="deliverySuburb"
              value={values.deliverySuburb}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableField
              required
              label="State"
              labelWidth="12rem"
              tag="deliveryState"
              touched={touched.deliveryState}
              error={errors.deliveryState}
              inputRenderer={(hasError: boolean) => (
                <Select
                  name="deliveryState"
                  value={values.deliveryState}
                  onChange={(_: any, value: string) => {
                    setFieldValue('deliveryState', value);
                  }}
                  options={[
                    { display: '---', tag: '' },
                    ...AUS_STATES.map(state => ({
                      display: state,
                      tag: state,
                    })),
                  ]}
                  error={hasError}
                />
              )}
            />
          </Cell>
          <Cell>
            <EditableInput
              required
              label="Postcode"
              labelWidth="12rem"
              tag="deliveryPostcode"
              value={values.deliveryPostcode}
              touched={touched}
              errors={errors}
              onBlur={onBlur}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <FormCheckbox
              id="sameBilling"
              saveValue={(_, val) => {
                setFieldValue('isSameBilling', val);
                setSameBilling(val);
              }}
              checked={isSameBilling}
              label="Same billing address as delivery address"
            />
          </Cell>
        </Row>
        {!isSameBilling && (
          <>
            <h3>Billing Address</h3>
            <Row>
              <Cell>
                <EditableInput
                  required
                  label="Street Address 1"
                  labelWidth="12rem"
                  tag="billingStreetAddress1"
                  value={values.billingStreetAddress1}
                  touched={touched}
                  errors={errors}
                  onBlur={onBlur}
                  onChange={setFieldValue}
                />
              </Cell>
            </Row>
            <Row>
              <Cell>
                <EditableInput
                  label="Street Address 2"
                  labelWidth="12rem"
                  tag="billingStreetAddress2"
                  value={values.billingStreetAddress2}
                  touched={touched}
                  errors={errors}
                  onBlur={onBlur}
                  onChange={setFieldValue}
                />
              </Cell>
            </Row>
            <Row>
              <Cell>
                <EditableInput
                  required
                  label="Suburb"
                  labelWidth="12rem"
                  tag="billingSuburb"
                  value={values.billingSuburb}
                  touched={touched}
                  errors={errors}
                  onBlur={onBlur}
                  onChange={setFieldValue}
                />
              </Cell>
            </Row>
            <Row>
              <Cell>
                <EditableField
                  required
                  label="State"
                  labelWidth="12rem"
                  tag="billingState"
                  touched={touched.billingState}
                  error={errors.billingState}
                  inputRenderer={(hasError: boolean) => (
                    <Select
                      name="billingState"
                      value={values.billingState}
                      onChange={(_: any, value: string) =>
                        setFieldValue('billingState', value)
                      }
                      options={[
                        { display: '---', tag: '' },
                        ...AUS_STATES.map(state => ({
                          display: state,
                          tag: state,
                        })),
                      ]}
                      error={hasError}
                    />
                  )}
                />
              </Cell>
              <Cell>
                <EditableInput
                  required
                  label="Postcode"
                  labelWidth="12rem"
                  tag="billingPostcode"
                  value={values.billingPostcode}
                  touched={touched}
                  errors={errors}
                  onBlur={onBlur}
                  onChange={setFieldValue}
                />
              </Cell>
            </Row>
          </>
        )}

        <Row>
          <CenterContainer>
            <TermsAndConditionContainer>
              <SubText>
                By creating a venue, you are accepting Ordermentum&apos;s
                <OrangeText
                  href="https://www.ordermentum.com/terms-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {' '}
                  Terms and Conditions
                </OrangeText>{' '}
                and
                <OrangeText
                  href="https://www.ordermentum.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {' '}
                  Privacy Policy
                </OrangeText>
              </SubText>
            </TermsAndConditionContainer>
          </CenterContainer>
        </Row>
        <CenterContainer>
          {isSubmitting || isRedirecting ? (
            <Button type="primary" size="large">
              <Spinner size="md" color="white" />
            </Button>
          ) : (
            <Button type="primary-submit" size="large">
              Get Started
            </Button>
          )}
        </CenterContainer>
      </form>
    );
  };

export const BusinessForm = ({ user }: Props) => {
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  return (
    <RetailerRegisterForm
      defaultValues={{ phone: user.phone, email: user.email }}
      onSuccessfulSubmission={() => {
        changeWindowLocation('/dashboard');
        setIsRedirecting(true);
      }}
    >
      {CustomRetailerRegisterForm({ isRedirecting })}
    </RetailerRegisterForm>
  );
};
